/**
 * OneSignal helpers — 4 team tags matching the PWA UI toggles.
 * lions | lionnes | academy (UI: u21) | club
 */

export type NotificationTeamKey = 'lions' | 'lionnes' | 'u21' | 'club'

/** UI team key → OneSignal tag key */
export const TEAM_ONESIGNAL_TAG: Record<NotificationTeamKey, string> = {
  lions: 'lions',
  lionnes: 'lionnes',
  u21: 'academy',
  club: 'club',
}

type OneSignalUser = {
  onesignalId?: string
  getTags(): Promise<Record<string, string>>
  addTags(tags: Record<string, string>): Promise<void>
  removeTags(keys: string[]): Promise<void>
  PushSubscription: {
    id?: string | null
    optedIn?: boolean
    optOut(): Promise<void>
    optIn(): Promise<void>
  }
}

type OneSignalSdk = {
  config?: { appId?: string }
  Notifications: {
    requestPermission(): Promise<boolean>
  }
  User: OneSignalUser
}

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: OneSignalSdk) => void | Promise<void>>
    OneSignal?: OneSignalSdk
  }
}

/**
 * Why notification preferences could not be read or saved:
 * - unsupported: no Notification API (iOS outside the installed app)
 * - permission: notifications blocked or refused
 * - unavailable: SDK or OneSignal's API did not answer
 * - unconfirmed: the write never showed up on OneSignal's side
 */
export type NotificationErrorReason = 'unsupported' | 'permission' | 'unavailable' | 'unconfirmed'

export class NotificationSyncError extends Error {
  constructor(public reason: NotificationErrorReason) {
    super(`OneSignal: ${reason}`)
  }
}

export const getOneSignal = (): Promise<OneSignalSdk> => {
  if (window.OneSignal) return Promise.resolve(window.OneSignal)

  return new Promise((resolve) => {
    window.OneSignalDeferred = window.OneSignalDeferred || []
    window.OneSignalDeferred.push(async (OneSignal) => {
      resolve(OneSignal)
    })
  })
}

/** SDK script blocked or never loaded (content blocker, flaky network). */
const SDK_TIMEOUT_MS = 10000

const getOneSignalOrFail = (): Promise<OneSignalSdk> =>
  Promise.race([
    getOneSignal(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new NotificationSyncError('unavailable')), SDK_TIMEOUT_MS),
    ),
  ])

/** Opt-in round-trip on mobile data — the previous 8s expired far too often. */
const SUBSCRIBE_TIMEOUT_MS = 20000

/** Read path: the settings toggles stay disabled until this resolves. */
const HYDRATION_TIMEOUT_MS = 10000

/**
 * Poll until OneSignal holds an actual push subscription record.
 *
 * `PushSubscription.id` is only set once the user model is hydrated (local cache,
 * then server), so it doubles as a readiness signal for `getTags()` — which reads
 * that same model synchronously and returns `{}` until then.
 */
const waitForPushSubscription = async (OneSignal: OneSignalSdk, timeoutMs: number) => {
  const started = Date.now()
  while (Date.now() - started < timeoutMs) {
    if (OneSignal.User.PushSubscription.id) return true
    await new Promise((r) => setTimeout(r, 150))
  }
  return Boolean(OneSignal.User.PushSubscription.id)
}

/** Tags currently known to OneSignal; `{}` when the model is not hydrated yet. */
const readTags = async (OneSignal: OneSignalSdk): Promise<Record<string, string>> => {
  try {
    return (await OneSignal.User.getTags()) || {}
  } catch {
    return {}
  }
}

/** Server read path: never let it hold the toggles for long. */
const SERVER_READ_TIMEOUT_MS = 5000

/**
 * Tags as stored by OneSignal's backend; `null` when they cannot be fetched.
 *
 * The SDK never refreshes its local tag model on page load, and that cached copy
 * can go stale for good (seen on iOS), so it must not be trusted on its own.
 */
const fetchServerTags = async (OneSignal: OneSignalSdk): Promise<Record<string, string> | null> => {
  const appId = OneSignal.config?.appId
  const onesignalId = OneSignal.User.onesignalId
  if (!appId || !onesignalId) return null

  try {
    const response = await fetch(
      `https://api.onesignal.com/apps/${appId}/users/by/onesignal_id/${onesignalId}`,
      { signal: AbortSignal.timeout(SERVER_READ_TIMEOUT_MS) },
    )
    if (!response.ok) return null
    const user = (await response.json()) as { properties?: { tags?: Record<string, string> } }
    return user.properties?.tags || {}
  } catch {
    return null
  }
}

export const notificationsFromTags = (
  tags: Record<string, string>,
): Record<NotificationTeamKey, boolean> => {
  const result = {
    lions: false,
    lionnes: false,
    u21: false,
    club: false,
  } as Record<NotificationTeamKey, boolean>

  ;(Object.keys(TEAM_ONESIGNAL_TAG) as NotificationTeamKey[]).forEach((team) => {
    result[team] = tags[TEAM_ONESIGNAL_TAG[team]] === '1'
  })

  return result
}

const matchesPreferences = (
  tags: Record<string, string>,
  preferences: Record<NotificationTeamKey, boolean>,
): boolean => {
  const saved = notificationsFromTags(tags)
  return (Object.keys(saved) as NotificationTeamKey[]).every((team) => saved[team] === preferences[team])
}

/** The SDK flushes its write queue every 500ms; leave room for slow mobile data. */
const CONFIRM_TIMEOUT_MS = 15000
const CONFIRM_INTERVAL_MS = 1000

/**
 * Poll OneSignal's API until it holds `preferences`.
 *
 * `addTags()` / `removeTags()` only update the SDK's local model and queue the
 * request, so returning from them proves nothing about what was saved.
 */
const confirmServerTags = async (
  OneSignal: OneSignalSdk,
  preferences: Record<NotificationTeamKey, boolean>,
): Promise<void> => {
  const started = Date.now()
  while (Date.now() - started < CONFIRM_TIMEOUT_MS) {
    await new Promise((r) => setTimeout(r, CONFIRM_INTERVAL_MS))
    const tags = await fetchServerTags(OneSignal)
    if (tags && matchesPreferences(tags, preferences)) return
  }
  throw new NotificationSyncError('unconfirmed')
}

/**
 * Request permission (if needed), sync team preferences → OneSignal tags, and
 * resolve once OneSignal's API confirms them. Throws a NotificationSyncError.
 */
export const syncNotificationPreferences = async (
  preferences: Record<NotificationTeamKey, boolean>,
): Promise<void> => {
  if (!('Notification' in window)) throw new NotificationSyncError('unsupported')

  const OneSignal = await getOneSignalOrFail()
  const wantsAny = Object.values(preferences).some(Boolean)

  if (wantsAny && Notification.permission === 'default') {
    await OneSignal.Notifications.requestPermission()
  }

  if (Notification.permission !== 'granted') {
    // Nothing to persist. Throw when the user asked to be notified, so callers
    // revert their optimistic toggle instead of showing a false "on".
    if (wantsAny) throw new NotificationSyncError('permission')
    return
  }

  if (wantsAny) {
    try {
      await OneSignal.User.PushSubscription.optIn()
    } catch {
      // Already opted in
    }
  }

  // Also gates the read below: computing the diff against a cold, empty tag
  // model would either skip a legitimate removal or wipe unrelated tags.
  const ready = await waitForPushSubscription(OneSignal, SUBSCRIBE_TIMEOUT_MS)
  if (!ready) {
    throw new NotificationSyncError('unconfirmed')
  }

  const local = await readTags(OneSignal)
  const server = (await fetchServerTags(OneSignal)) || {}
  const toAdd: Record<string, string> = {}
  const toRemove: string[] = []

  ;(Object.keys(TEAM_ONESIGNAL_TAG) as NotificationTeamKey[]).forEach((team) => {
    const tag = TEAM_ONESIGNAL_TAG[team]
    if (preferences[team]) toAdd[tag] = '1'
    // Only remove what is actually set, so a stale read can never destroy tags.
    // The local model matters too: the SDK sends its whole tag map on every write.
    else if (local[tag] === '1' || server[tag] === '1') toRemove.push(tag)
  })

  if (Object.keys(toAdd).length) await OneSignal.User.addTags(toAdd)
  if (toRemove.length) await OneSignal.User.removeTags(toRemove)

  await confirmServerTags(OneSignal, preferences)
}

export type NotificationPreferencesResult = {
  notifications: Record<NotificationTeamKey, boolean>
  /** Set when `notifications` is not OneSignal's confirmed state. */
  error: NotificationErrorReason | null
}

export const loadNotificationPreferences = async (): Promise<NotificationPreferencesResult> => {
  const empty = {
    lions: false,
    lionnes: false,
    u21: false,
    club: false,
  } as Record<NotificationTeamKey, boolean>

  if (!('Notification' in window)) return { notifications: empty, error: 'unsupported' }
  if (Notification.permission === 'denied') return { notifications: empty, error: 'permission' }
  // Never asked yet: nothing can be subscribed.
  if (Notification.permission !== 'granted') return { notifications: empty, error: null }

  try {
    const OneSignal = await getOneSignalOrFail()
    // Without this the model is often still cold and every team reads as off,
    // even though the user is subscribed server-side.
    await waitForPushSubscription(OneSignal, HYDRATION_TIMEOUT_MS)
    const tags = await fetchServerTags(OneSignal)
    if (tags) return { notifications: notificationsFromTags(tags), error: null }

    return { notifications: notificationsFromTags(await readTags(OneSignal)), error: 'unavailable' }
  } catch {
    return { notifications: empty, error: 'unavailable' }
  }
}
